import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'lib/dayjs';
import {
  useGetLunchBreakTracksQuery,
  useApproveLunchBreakTrackMutation,
  useRejectLunchBreakTrackMutation,
  useBulkApproveLunchBreakTracksMutation,
  useBulkRejectLunchBreakTracksMutation,
} from 'redux/features/lunchBreakTrackApi';
import { toast } from 'react-toastify';
import ConfirmationModal from './ConfirmationModal';
import Icon from '../AppIcon';

const statusConfig = {
  PENDING: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    icon: 'Clock',
    iconColor: 'text-amber-500',
  },
  APPROVED: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    badgeBg: 'bg-green-100',
    icon: 'CheckCircle',
    iconColor: 'text-green-500',
  },
  REJECTED: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    badgeBg: 'bg-red-100',
    icon: 'XCircle',
    iconColor: 'text-red-500',
  },
};

/* ─── Employee Detail Modal ─────────────────────────────────────────── */
const EmployeeDetailModal = ({
  isOpen,
  onClose,
  employee,
  tracks,
  isAdmin,
  onApprove,
  onReject,
  processingId,
}) => {
  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl z-10 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-orange-100">
                <Icon name="UtensilsCrossed" size={20} className="text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {employee.first_name} {employee.last_name}
                </h3>
                <p className="text-sm text-gray-500">
                  {tracks.length} lunch break record{tracks.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
            {tracks.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-center">
                <Icon name="UtensilsCrossed" size={32} className="text-gray-300 mb-2" />
                <p className="text-gray-400 text-sm">No records found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {tracks.map((track) => {
                  const cfg = statusConfig[track.status] || statusConfig.PENDING;
                  return (
                    <div key={track.id} className="p-4 hover:bg-gray-50/60 transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`p-1.5 rounded-lg ${cfg.badgeBg} shrink-0`}>
                            <Icon name="UtensilsCrossed" size={14} className={cfg.iconColor} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800">
                              {dayjs(track.date).format('MMM D, YYYY')}
                            </p>
                            <p className="text-xs text-gray-500">
                              {track.worked_minutes} min ·{' '}
                              {track.lunch_work_status === 'FULL' ? 'Full Break' : 'Half Break'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeBg} ${cfg.text}`}
                          >
                            <Icon name={cfg.icon} size={10} />
                            {track.status}
                          </span>
                          {isAdmin && track.status === 'PENDING' && processingId !== track.id && (
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => onApprove(track)}
                                className="px-2.5 py-1 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors border border-green-200"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => onReject(track)}
                                className="px-2.5 py-1 rounded-lg bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors border border-red-200"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                          {processingId === track.id && (
                            <div className="h-4 w-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

/* ─── Employee Group Card ────────────────────────────────────────────── */
const EmployeeGroupCard = ({
  employeeData,
  activeTab,
  isAdmin,
  onViewDetails,
  onApprove,
  onReject,
  viewMode,
}) => {
  const tracks = employeeData.tracks;
  const employee = employeeData.employee;
  const pendingCount = tracks.filter((t) => t.status === 'PENDING').length;
  const approvedCount = tracks.filter((t) => t.status === 'APPROVED').length;
  const rejectedCount = tracks.filter((t) => t.status === 'REJECTED').length;
  const totalMinutes = tracks.reduce((sum, t) => sum + t.worked_minutes, 0);

  return (
    <div className="flex items-center justify-between gap-4 p-3 rounded-xl border border-gray-200 bg-white hover:border-primary/30 hover:shadow-sm transition-all">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white text-sm font-bold shrink-0 uppercase">
          {employee?.first_name?.[0]}
          {employee?.last_name?.[0]}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 capitalize truncate">
            {employee?.first_name} {employee?.last_name}
          </p>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {pendingCount > 0 && (
              <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-md font-medium">
                {pendingCount} Pending
              </span>
            )}
            {approvedCount > 0 && (
              <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded-md font-medium">
                {approvedCount} Approved
              </span>
            )}
            {rejectedCount > 0 && (
              <span className="text-xs text-red-600 bg-red-50 px-1.5 py-0.5 rounded-md font-medium">
                {rejectedCount} Rejected
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right hidden sm:block">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-sm font-bold text-gray-800">{totalMinutes} min</p>
        </div>

        {isAdmin && viewMode === 'day' && activeTab === 'PENDING' ? (
          <div className="flex gap-1.5 shrink-0">
            <button
              onClick={() => onApprove(tracks.filter((t) => t.status === 'PENDING'))}
              className="px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors border border-green-200"
            >
              Approve
            </button>
            <button
              onClick={() => onReject(tracks.filter((t) => t.status === 'PENDING'))}
              className="px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors border border-red-200"
            >
              Reject
            </button>
          </div>
        ) : (
          <button
            onClick={() =>
              onViewDetails(employeeData.employee?.id ?? employeeData.tracks[0]?.employee_id)
            }
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors border border-primary/20"
          >
            <Icon name="Eye" size={13} />
            View Details
          </button>
        )}
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────── */
const LunchBreakTrackerList = ({
  employeeId = null,
  isAdmin = true,
  groupByEmployee = false,
  compact = false,
}) => {
  const [activeTab, setActiveTab] = useState('PENDING');
  const [viewMode, setViewMode] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [expandedId, setExpandedId] = useState(null);
  const [approveModalData, setApproveModalData] = useState(null);
  const [rejectModalData, setRejectModalData] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Build query params — use viewMode to decide between month and date
  const queryParams = {};
  if (employeeId) queryParams.employee_id = employeeId;

  if (viewMode === 'monthly') {
    queryParams.month = selectedMonth;
    queryParams.year = selectedYear;
  } else {
    queryParams.date = selectedDate;
  }
  // When groupByEmployee, fetch all statuses; otherwise use tab
  if (!groupByEmployee) {
    queryParams.status = activeTab;
  }

  const { data: tracks = [], isLoading } = useGetLunchBreakTracksQuery(queryParams);
  const [approveTrack] = useApproveLunchBreakTrackMutation();
  const [rejectTrack] = useRejectLunchBreakTrackMutation();
  const [bulkApprove] = useBulkApproveLunchBreakTracksMutation();
  const [bulkReject] = useBulkRejectLunchBreakTracksMutation();

  const [bulkActionData, setBulkActionData] = useState(null); // { type: 'approve' | 'reject', ids: number[] }

  // Group tracks by employee when in groupByEmployee mode
  const groupedByEmployee = useMemo(() => {
    if (!groupByEmployee) return null;
    const map = {};
    tracks.forEach((track) => {
      const empId = track.employee_id;
      if (!map[empId]) {
        map[empId] = { employee: track.employee, tracks: [] };
      }
      map[empId].tracks.push(track);
    });
    // Sort by pending count desc
    return Object.values(map).sort(
      (a, b) =>
        b.tracks.filter((t) => t.status === 'PENDING').length -
        a.tracks.filter((t) => t.status === 'PENDING').length,
    );
  }, [tracks, groupByEmployee]);

  const filteredGrouped = useMemo(() => {
    if (!groupedByEmployee) return null;
    let result = groupedByEmployee;
    // Filter by status tab
    if (activeTab !== 'ALL') {
      result = result
        .map((g) => ({ ...g, tracks: g.tracks.filter((t) => t.status === activeTab) }))
        .filter((g) => g.tracks.length > 0);
    }
    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((g) => {
        const name = `${g.employee?.first_name ?? ''} ${g.employee?.last_name ?? ''}`.toLowerCase();
        return name.includes(q);
      });
    }
    return result;
  }, [groupedByEmployee, activeTab, searchQuery]);

  // Derive the selected employee's live data from grouped data
  const selectedEmployeeData = useMemo(() => {
    if (!selectedEmployeeId || !groupedByEmployee) return null;
    return (
      groupedByEmployee.find(
        (g) => (g.employee?.id ?? g.tracks[0]?.employee_id) === selectedEmployeeId,
      ) ?? null
    );
  }, [selectedEmployeeId, groupedByEmployee]);

  const handleApprove = async () => {
    if (!approveModalData) return;
    setProcessingId(approveModalData.id);
    try {
      await approveTrack(approveModalData.id).unwrap();
      toast.success('Lunch break track approved');
      setApproveModalData(null);
    } catch {
      console.error('Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModalData) return;
    setProcessingId(rejectModalData.id);
    try {
      await rejectTrack({
        id: rejectModalData.id,
        rejection_reason: rejectReason || undefined,
      }).unwrap();
      toast.success(
        `${rejectModalData.lunch_work_status === 'FULL' ? 'Log split' : 'Time removed'} successfully`,
      );
      setRejectModalData(null);
      setRejectReason('');
    } catch {
      console.error('Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (!bulkActionData || bulkActionData.type !== 'approve') return;
    try {
      await bulkApprove(bulkActionData.ids).unwrap();
      toast.success(`${bulkActionData.ids.length} lunch breaks approved`);
      setBulkActionData(null);
    } catch {
      toast.error('Failed to bulk approve');
    }
  };

  const handleBulkReject = async () => {
    if (!bulkActionData || bulkActionData.type !== 'reject') return;
    try {
      await bulkReject({
        ids: bulkActionData.ids,
        rejection_reason: rejectReason || undefined,
      }).unwrap();
      toast.success(`${bulkActionData.ids.length} lunch breaks rejected`);
      setBulkActionData(null);
      setRejectReason('');
    } catch {
      toast.error('Failed to bulk reject');
    }
  };

  // Helper to trigger approve all for current filtered list
  const triggerApproveAll = () => {
    const pendingIds = [];
    filteredGrouped.forEach((g) => {
      g.tracks.forEach((t) => {
        if (t.status === 'PENDING') pendingIds.push(t.id);
      });
    });
    if (pendingIds.length === 0) return;
    setBulkActionData({ type: 'approve', ids: pendingIds });
  };

  // Helper to trigger reject all for current filtered list
  const triggerRejectAll = () => {
    const pendingIds = [];
    filteredGrouped.forEach((g) => {
      g.tracks.forEach((t) => {
        if (t.status === 'PENDING') pendingIds.push(t.id);
      });
    });
    if (pendingIds.length === 0) return;
    setBulkActionData({ type: 'reject', ids: pendingIds });
  };

  // Trigger individual card actions (might involve multiple tracks for one user)
  const handleCardApprove = (userTracks) => {
    const ids = userTracks.filter((t) => t.status === 'PENDING').map((t) => t.id);
    if (ids.length === 1) {
      setApproveModalData(userTracks[0]);
    } else if (ids.length > 1) {
      setBulkActionData({ type: 'approve', ids });
    }
  };

  const handleCardReject = (userTracks) => {
    const ids = userTracks.filter((t) => t.status === 'PENDING').map((t) => t.id);
    if (ids.length === 1) {
      setRejectModalData(userTracks[0]);
    } else if (ids.length > 1) {
      setBulkActionData({ type: 'reject', ids });
    }
  };

  const handleMonthNav = (direction) => {
    let m = selectedMonth + direction;
    let y = selectedYear;
    if (m < 1) {
      m = 12;
      y -= 1;
    }
    if (m > 12) {
      m = 1;
      y += 1;
    }
    setSelectedMonth(m);
    setSelectedYear(y);
  };

  const handleDayNav = (direction) => {
    setSelectedDate((prev) => dayjs(prev).add(direction, 'day').format('YYYY-MM-DD'));
  };

  const Tabs = groupByEmployee
    ? [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' },
        { label: 'All', value: 'ALL' },
      ]
    : [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' },
      ];

  const displayTracks = groupByEmployee ? [] : tracks;
  const isEmpty = groupByEmployee ? filteredGrouped?.length === 0 : displayTracks.length === 0;

  return (
    <div className="w-full">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {Tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-4 py-2 text-sm font-semibold transition-all border-b-2 focus:outline-none outline-none ${
              activeTab === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {activeTab === tab.value && !isLoading && (
              <span className="ml-2 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px]">
                {groupByEmployee ? (filteredGrouped?.length ?? 0) : tracks.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters & View Mode Switch */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* View Mode Switch - Only show if we're in "all employees" view (like Dashboard) */}
          {isAdmin && !employeeId && (
            <div className="flex p-1 bg-secondary-100 rounded-lg border border-border">
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  viewMode === 'monthly'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  viewMode === 'day'
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                Day
              </button>
            </div>
          )}

          {viewMode === 'monthly' ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleMonthNav(-1)}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
                {dayjs(`${selectedYear}-${selectedMonth}-01`).format('MMMM YYYY')}
              </span>
              <button
                onClick={() => handleMonthNav(1)}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleDayNav(-1)}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <Icon name="ChevronLeft" size={16} />
              </button>
              <div className="relative group">
                <input
                  type="date"
                  max={dayjs().format('YYYY-MM-DD')}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-gray-700 bg-white"
                />
                <div className="absolute left-1/2 -bottom-5 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                  <span className="text-[10px] text-text-tertiary">
                    {dayjs(selectedDate).format('dddd')}
                  </span>
                </div>
              </div>
              <button
                onClick={() => handleDayNav(1)}
                disabled={dayjs(selectedDate).isSame(dayjs(), 'day')}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Icon name="ChevronRight" size={16} />
              </button>
              <span className="text-xs text-text-secondary min-w-[70px]">
                {dayjs(selectedDate).isSame(dayjs().subtract(1, 'day'), 'day')
                  ? '(Yesterday)'
                  : dayjs(selectedDate).isSame(dayjs(), 'day')
                    ? '(Today)'
                    : ''}
              </span>
            </div>
          )}
        </div>
        {groupByEmployee && (
          <div className="flex items-center gap-2">
            {isAdmin &&
              viewMode === 'day' &&
              activeTab === 'PENDING' &&
              (filteredGrouped?.length ?? 0) > 0 && (
                <div className="flex items-center gap-2 mr-2">
                  <button
                    onClick={triggerApproveAll}
                    className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700 transition-colors shadow-sm whitespace-nowrap"
                  >
                    Approve All
                  </button>
                  <button
                    onClick={triggerRejectAll}
                    className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-colors shadow-sm whitespace-nowrap"
                  >
                    Reject All
                  </button>
                </div>
              )}
            <div className="relative">
              <Icon
                name="Search"
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary w-32 sm:w-44"
              />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : isEmpty ? (
        <div className="py-10 flex flex-col items-center justify-center text-center">
          <div className="p-3 rounded-full bg-gray-100 mb-3">
            <Icon name="UtensilsCrossed" size={24} className="text-gray-400" />
          </div>
          <h4 className="text-sm font-bold text-gray-500">No lunch break records found</h4>
          <p className="text-xs text-gray-400 mt-1">
            No records for{' '}
            {viewMode === 'monthly'
              ? dayjs(`${selectedYear}-${selectedMonth}-01`).format('MMMM YYYY')
              : dayjs(selectedDate).format('MMM D, YYYY')}
          </p>
        </div>
      ) : groupByEmployee ? (
        /* Employee grouped view */
        <div className="space-y-2.5">
          {filteredGrouped.map((empData) => (
            <EmployeeGroupCard
              key={empData.employee?.id ?? empData.tracks[0]?.employee_id}
              employeeData={empData}
              activeTab={activeTab}
              isAdmin={isAdmin}
              viewMode={viewMode}
              onViewDetails={setSelectedEmployeeId}
              onApprove={handleCardApprove}
              onReject={handleCardReject}
              processingId={processingId}
            />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className={`${compact ? 'hidden' : 'hidden md:block'}`}>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {!employeeId && (
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Employee</th>
                    )}
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">
                      Worked Minutes
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                    {isAdmin && (
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayTracks.map((track) => {
                    const cfg = statusConfig[track.status] || statusConfig.PENDING;
                    return (
                      <tr key={track.id} className="hover:bg-gray-50/50 transition-colors">
                        {!employeeId && (
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-800 capitalize">
                              {track.employee?.first_name} {track.employee?.last_name}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3 text-gray-600">
                          {dayjs(track.date).format('MMM D, YYYY')}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-800">
                            {track.worked_minutes} min
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeBg} ${cfg.text}`}
                          >
                            <Icon name={cfg.icon} size={12} className={cfg.iconColor} />
                            {track.status}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3">
                            {track.status === 'PENDING' && processingId !== track.id ? (
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => setApproveModalData(track)}
                                  className="px-3 py-1 rounded-lg bg-green-50 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors border border-green-200"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => setRejectModalData(track)}
                                  className="px-3 py-1 rounded-lg bg-red-50 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors border border-red-200"
                                >
                                  Reject
                                </button>
                              </div>
                            ) : processingId === track.id ? (
                              <div className="flex justify-center">
                                <div className="h-4 w-4 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 text-center block">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className={`${compact ? 'block' : 'block md:hidden'} space-y-3`}>
            {displayTracks.map((track) => {
              const cfg = statusConfig[track.status] || statusConfig.PENDING;
              const isExpanded = expandedId === track.id;
              return (
                <motion.div
                  key={track.id}
                  className={`rounded-xl border ${cfg.border} ${cfg.bg} overflow-hidden transition-all`}
                  layout
                >
                  <div
                    className="p-3 cursor-pointer flex items-center justify-between"
                    onClick={() => setExpandedId(isExpanded ? null : track.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-2 rounded-lg ${cfg.badgeBg} shrink-0`}>
                        <Icon name="UtensilsCrossed" size={16} className={cfg.iconColor} />
                      </div>
                      <div className="min-w-0">
                        {!employeeId && (
                          <p className="font-semibold text-sm text-gray-800 capitalize truncate">
                            {track.employee?.first_name} {track.employee?.last_name}
                          </p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{dayjs(track.date).format('MMM D, YYYY')}</span>
                          <span>·</span>
                          <span className="font-semibold text-gray-700">
                            {track.worked_minutes} min
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeBg} ${cfg.text}`}
                      >
                        <Icon name={cfg.icon} size={10} />
                        {track.status}
                      </span>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-gray-200/50">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="text-xs text-gray-500">Lunch Work Status</div>
                            <div className="text-xs font-semibold text-gray-700">
                              {track.lunch_work_status === 'FULL'
                                ? 'Full Break Worked'
                                : 'Half Break Worked'}
                            </div>
                          </div>
                          {isAdmin && track.status === 'PENDING' && processingId !== track.id && (
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setApproveModalData(track);
                                }}
                                className="flex-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
                              >
                                Approve
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRejectModalData(track);
                                }}
                                className="flex-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </>
      )}

      {/* Employee Detail Modal */}
      <AnimatePresence>
        {selectedEmployeeId && selectedEmployeeData && (
          <EmployeeDetailModal
            isOpen={true}
            onClose={() => setSelectedEmployeeId(null)}
            employee={selectedEmployeeData.employee}
            tracks={selectedEmployeeData.tracks}
            isAdmin={isAdmin}
            onApprove={setApproveModalData}
            onReject={setRejectModalData}
            processingId={processingId}
          />
        )}
      </AnimatePresence>

      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!approveModalData}
        onClose={() => setApproveModalData(null)}
        onConfirm={handleApprove}
        title="Approve Lunch Work"
        message={`Approve lunch work for ${approveModalData?.employee?.first_name} ${approveModalData?.employee?.last_name}? The original time log will remain unchanged.`}
      />

      {/* Reject Modal */}
      {rejectModalData && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => {
              setRejectModalData(null);
              setRejectReason('');
            }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl z-10">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-100">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Lunch Work</h3>
                </div>
                <button
                  onClick={() => {
                    setRejectModalData(null);
                    setRejectReason('');
                  }}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-800">
                    <strong>Warning:</strong> Rejecting will{' '}
                    {rejectModalData.lunch_work_status === 'FULL'
                      ? 'split the time log'
                      : 'remove the lunch work time'}{' '}
                    for{' '}
                    <strong className="capitalize">
                      {rejectModalData.employee?.first_name} {rejectModalData.employee?.last_name}
                    </strong>
                    , and update their total working hours.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-gray-400">(Optional)</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 resize-none focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 p-5 border-t border-gray-100">
                <button
                  onClick={() => {
                    setRejectModalData(null);
                    setRejectReason('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processingId === rejectModalData?.id}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {processingId === rejectModalData?.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    'Reject'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={bulkActionData?.type === 'approve'}
        onClose={() => setBulkActionData(null)}
        onConfirm={handleBulkApprove}
        title="Bulk Approve Lunch Work"
        message={`Are you sure you want to approve ${bulkActionData?.ids?.length} lunch break tracks?`}
      />

      {/* Bulk Reject Modal */}
      {bulkActionData?.type === 'reject' && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => {
              setBulkActionData(null);
              setRejectReason('');
            }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl z-10">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-red-100">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Bulk Reject Lunch Work</h3>
                </div>
                <button
                  onClick={() => {
                    setBulkActionData(null);
                    setRejectReason('');
                  }}
                  className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <p className="text-sm text-red-800 font-medium">
                    You are about to reject {bulkActionData.ids.length} lunch break tracks.
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    This will split the corresponding time logs and remove the lunch time for all
                    affected employees.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection <span className="text-gray-400">(Optional)</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Provide a reason for rejection..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 resize-none focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 p-5 border-t border-gray-100">
                <button
                  onClick={() => {
                    setBulkActionData(null);
                    setRejectReason('');
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkReject}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                >
                  Reject All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LunchBreakTrackerList;
